
import {
  defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Skeleton from 'primevue/skeleton';
import Tooltip from 'primevue/tooltip';

import {
  getContactProfileSourceLabel,
  getContactProfileState,
  getContactSourceProfilesNest,
} from '@/composables/data-explorer/contactSourceProfiles';
import { formatDate } from '@/helpers/Date';
import { ContactSourceProfile } from '@/types/data-explorer-types';

/**
 * Fiches client d'un contact, une par point de vente, avec leur identifiant
 * dans ce point de vente. Lecture seule : ni fusion ni action.
 */
export default defineComponent({
  name: 'ContactSourceProfiles',

  components: {
    Skeleton,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idShopContact: {
      type: Number,
      required: true,
    },
  },

  emits: ['loaded'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const profiles = ref<ContactSourceProfile[]>([]);
    const loading = ref(true);
    const loadError = ref(false);

    // La fiche contact est réutilisée d'un onglet à l'autre : seule la réponse
    // du dernier contact demandé est gardée.
    let lastRequest = 0;

    const load = async (idShopContact: number) => {
      lastRequest += 1;
      const request = lastRequest;
      loading.value = true;
      loadError.value = false;

      const result = idShopContact ? await getContactSourceProfilesNest(idShopContact) : [];
      if (request !== lastRequest) {
        return;
      }

      profiles.value = result ?? [];
      loadError.value = result === null;
      loading.value = false;
      emit('loaded', profiles.value.length);
    };

    watch(() => props.idShopContact, (idShopContact) => {
      load(idShopContact);
    }, { immediate: true });

    const getProfileIcon = (profile: ContactSourceProfile): string => {
      if (profile.sourceResolution === 'import') {
        return 'fa-light fa-file-import';
      }

      return profile.source && profile.source.parent ? 'fa-light fa-store' : 'fa-light fa-database';
    };

    return {
      t,
      profiles,
      loading,
      loadError,
      formatDate,
      getContactProfileSourceLabel,
      getContactProfileState,
      getProfileIcon,
    };
  },
});
